import React, { useState, useEffect } from "react";
import "./analysis.scss";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { saveAs } from "file-saver"; // File saver library for exporting CSV

export const Analysis = () => {
  const [evalData, setEvalData] = useState([]);
  const [userToken, setUserToken] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [searchQuery, setSearchQuery] = useState(""); // For filtering firstname, lastname, and link
  const [expandedEval, setExpandedEval] = useState(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  const getUserToken = async () => {
    try {
      const session = await fetchAuthSession();
      const access_token = session.tokens.accessToken.toString();
      setUserToken(access_token);
      return access_token;
    } catch {
      navigate("/login");
    }
  };

  useEffect(() => {
    getUserToken();
  }, []);

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!userToken) return;

      setIsAnalysisLoading(true);

      try {
        const response = await fetch("https://api.weathervaneintel.com/api/api/getanalysis", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            access_token: "example_user_token",
          },
          body: JSON.stringify([{ userToken, startDate }]),
        });

        if (response.status === 403) {
          // Redirect unauthorized user to login
          navigate("/login");
          return;
        }

        if (!response.ok) {
          console.warn("API Error:", response.status, response.statusText);
          setIsAnalysisLoading(false);
          return;
        }

        const data = await response.json();
        setEvalData(data);
        setIsAnalysisLoading(false);

      } catch (err) {
        console.error("Network or fetch error:", err);
        setIsAnalysisLoading(false);
      }
    };

    fetchAnalysis();
  }, [userToken, startDate]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const toggleAnalysis = (index) => {
    setExpandedEval(expandedEval === index ? null : index);
  };


  const filterData = () => {
    return evalData.filter((row) => {
      const matchesDate =
        !startDate || new Date(row.comment_date) >= new Date(startDate);

      const matchesSearch =
        !searchQuery ||
        row.firstname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.lastname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.link.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesDate && matchesSearch;
    });
  };

  const filtered = filterData();

  const reasonCounts = filtered.reduce((acc, row) => {
    acc[row.reason] = (acc[row.reason] || 0) + 1;
    return acc;
  }, {});


  const handleExportCSV = () => {
    const headers = [
      "Eval Type",
      "Reason",
      "Eval Date",
      "Username",
      "Link",
      "Comment",
      "Full Analysis",
    ];

    const csvRows = [
      headers,
      ...filterData().map((row) => [
        row.type,
        row.reason,
        row.eval_date,
        row.username,
        row.link,
        row.comment,
        row.full_analysis.replace(/\n/g, " "), // Clean up newlines in the analysis
      ]),
    ];

    const csvContent = csvRows
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "evaluations.csv");
  };

  if (userToken === null) {
    return (
      <div className="loadingdiv">
        <h1>Initializing Secure Session</h1>
        <p>Verifying credentials…</p>
      </div>
    );
  }

  if (isAnalysisLoading) {
    return (
      <div className="loadingdiv">
        <h1>Processing Analysis</h1>
        <p>Compiling intelligence and preparing results…</p>

        <div className="loadingSkeleton">
          <div className="skeletonCard" />
          <div className="skeletonCard" />
          <div className="skeletonCard" />
        </div>
      </div>
    );
  }

  return (
    <div className="analysishomecontainer">
      <div className="analysispage longButtonStacked">
        <div className="summaryPanel">
          <div className="summaryTotal">
            Total Evaluations: <strong>{filtered.length}</strong>
          </div>

          <div className="summaryBreakdown">
            {Object.entries(reasonCounts).map(([reason, count]) => (
              <span
                key={reason}
                className={`reasonBadge reason${reason.replace(/,/g, '').replace(/\s/g, '')}`}
              >
                {reason}: <strong>{count}</strong>
              </span>
            ))}
          </div>

        </div>

        <div className="filters">
          <button
            className="filterToggleMobile"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>

          <div className={`filterLeft ${showFilters ? "visible" : "hiddenMobile"}`}>
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className="filterInput"
            />
            <input
              type="text"
              placeholder="Search by Name or Link"
              value={searchQuery}
              onChange={handleSearchChange}
              className="filterInput"
            />
          </div>

          <button className="shortButton" onClick={handleExportCSV}>
            Export Data to CSV
          </button>
        </div>

        <div className="cardContainer">
          {filtered.map((row, index) => (
            <div className="evaluationCard" key={index}>
              <div className="cardHeader">
                <h3>{row.type}</h3>
                <span className="date">Comment Date: {row.comment_date}</span>
              </div>

              <div className="cardMeta">
                <strong>Reason:</strong>{" "}
                <span className={`reasonBadge reason${row.reason.replace(/,/g, '').replace(/\s/g, '')}`}>
                  {row.reason}
                </span>
                <br />
                <strong>Source:</strong>{" "}
                <a href={row.link} target="_blank" rel="noopener noreferrer" className="sourceLink">
                  View Source
                </a>
              </div>

              <div className="cardContent">
                <div className="commentBlock">{row.comment}</div>

                <button className="toggleAnalysis" onClick={() => toggleAnalysis(index)}>
                  {expandedEval === index ? "Hide Full Analysis" : "View Full Analysis"}
                </button>

                {expandedEval === index && (
                  <div className="analysisBlock">{row.full_analysis}</div>
                )}
              </div>

              <div className="cardFooter">
                <span className="user">{row.firstname} {row.lastname}</span>
                <span className="evalDate">Eval Date: {row.eval_date}</span>
              </div>
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};
